/** Calendar view of one uninterrupted recorded work interval; shared session labels link pieces
 * while actual pause gaps remain empty. Recorded history cannot be dragged or restarted.
 * @module apps/web/src/components/calendar/RecordedWorkSegment
 */
import type { ScheduleBlock } from '@fluxability/types';
import { Link2 } from 'lucide-react';
import { laneStyle } from './timelineLanes';
import { ActivityDone } from '@/components/activities/ActivityDone';

/** Render a recorded piece using timeline percentage geometry and an accessible session label. */
export function RecordedWorkSegment({ block, onClick, lane, lanes, done }: {
  /** Canonical recorded interval and session linkage. */
  block: ScheduleBlock;
  /** Canonical completion from the current owning activity, never inferred from recorded work. */
  done: boolean;
  /** Open the activity's history/editor. */
  onClick: () => void;
  /** Overlap lane. */
  lane?: number;
  /** Total overlap lanes. */
  lanes?: number;
}) {
  const start = new Date(block.startTime);
  const end = new Date(block.endTime);
  const piece = (block.workSegmentIndex ?? 0) + 1;
  const total = block.workSegmentCount ?? 1;
  const label = `${block.title} · recorded work · part ${piece} of ${total}${done ? ' · Done' : ''}`;
  const topSeconds = start.getMinutes() * 60 + start.getSeconds() + start.getMilliseconds() / 1000;
  return <div data-work-span={block.id}
    className="absolute left-0 right-0 overflow-hidden focus-within:outline focus-within:outline-1 focus-within:outline-primary"
    style={{ top: `${topSeconds / 3600 * 100}%`, height: `${(end.getTime() - start.getTime()) / 36000}%`, ...laneStyle(lane, lanes) }}>
    <button type="button" onClick={onClick} aria-label={label}
      title={`${label}\n${start.toLocaleTimeString()} – ${end.toLocaleTimeString()}\nSession ${block.sessionId}`}
      data-session-id={block.sessionId} data-work-segment={piece}
      className="block h-full w-full min-h-0 overflow-hidden rounded-md border border-emerald-500/50 border-l-4 bg-emerald-500/15 px-1 text-left text-xs text-foreground focus-visible:outline-none">
      <span className="flex items-center gap-1 truncate"><Link2 className="h-3 w-3 shrink-0" />{done && <ActivityDone compact />}{block.title}</span>
      <span className="text-[10px] opacity-75">Recorded · {piece}/{total}</span>
    </button>
  </div>;
}
